import { type ReactNode, useMemo } from "react";
import {
    useReactTable,
    getCoreRowModel,
    type ColumnDef,
    type SortingState,
    type VisibilityState,
} from "@tanstack/react-table";

import { useDebouncedSearchInput } from "~/shared/hooks/useDebouncedSearchInput";
import { useIsMobile } from "~/shared/lib/use-media-query";
import { type FilterFieldConfig } from "~/shared/ui/data-table/DataTableFilter";
import DataTablePagination from "~/shared/ui/data-table/DataTablePagination";

import { buildSectionColumns } from "./section-columns";
import { SectionContent } from "./SectionContent";
import { type SectionCardContext } from "./section-types";
import { SectionToolbar } from "./SectionToolbar";
import { type SectionView } from "./SectionViewSwitcher";

export interface SectionDataViewProps<TRow extends { id: string }> {
    columns: ColumnDef<TRow, unknown>[];
    data: readonly TRow[];
    total: number;
    isLoading?: boolean;
    isError?: boolean;

    /** View mode (controlled, URL-backed). Forced to "card" below the md breakpoint. */
    view: SectionView;
    onViewChange: (view: SectionView) => void;

    /** Server-side pagination. pageIndex is 0-based. `pageSize` is the EFFECTIVE size
     *  fetched (column-rounded in card/grid); pass the user's target via `selectorPageSize`. */
    pageIndex: number;
    pageSize: number;
    onPaginationChange: (pageIndex: number, pageSize: number) => void;
    pageSizeOptions?: number[];
    /** Target page size shown in the rows-per-page selector (defaults to `pageSize`). */
    selectorPageSize?: number;

    /** Server-side sorting, persisted by the caller. */
    sorting: SortingState;
    onSortingChange: (s: SortingState) => void;

    /** Column visibility, persisted by the caller. */
    columnVisibility: VisibilityState;
    onColumnVisibilityChange: (v: VisibilityState) => void;

    /** Search (controlled, URL-backed). */
    query: string;
    onQueryChange: (q: string) => void;
    /** Show the search input. Default true; pass false when the backend has no text search. */
    searchable?: boolean;

    /** Expanding filter panel (not a popover). */
    filterFields: FilterFieldConfig[];
    filterValues: Record<string, unknown>;
    onFilterChange: (key: string, value: unknown) => void;
    onFilterApply: () => void;
    onFilterReset: () => void;
    filterOpen: boolean;
    onFilterOpenChange: (open: boolean) => void;
    activeFilterCount?: number;

    /** Card renderer for the card view. */
    renderCard: (row: TRow, ctx: SectionCardContext) => ReactNode;

    /** Dense-grid tile renderer. When provided, a third "grid" view becomes available. */
    renderGridTile?: (row: TRow, ctx: SectionCardContext) => ReactNode;

    /** Selection (works in both views). */
    selecting?: boolean;
    selectedIds?: ReadonlySet<string>;
    onToggleSelected?: (id: string) => void;
    /** All selectable ids across every page (for the header select-all). */
    allIds?: readonly string[];
    /** Select / clear all `allIds`. Enables the header select-all checkbox. */
    onSelectAll?: (selected: boolean) => void;
    selectAllLabel?: string;

    onRowClick?: (row: TRow) => void;
    getRowClassName?: (row: TRow) => string | undefined;

    /** Eye cross-nav target handler (read off column meta by row renderers). */
    onEyeNavigate?: (targetTab: string, id: string, label: string) => void;

    emptyState?: ReactNode;
    errorState?: ReactNode;

    /* i18n labels */
    searchPlaceholder?: string;
    clearSearchLabel?: string;
    filterLabel?: string;
    applyLabel?: string;
    resetLabel?: string;
    columnsLabel?: string;
    sortLabel?: string;
    toggleSortDirLabel?: string;
    showingLabel?: string;
    rowsPerPageLabel?: string;
}

export function SectionDataView<TRow extends { id: string }>({
    columns,
    data,
    total,
    isLoading,
    isError,
    view,
    onViewChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    pageSizeOptions,
    selectorPageSize,
    sorting,
    onSortingChange,
    columnVisibility,
    onColumnVisibilityChange,
    query,
    onQueryChange,
    searchable = true,
    filterFields,
    filterValues,
    onFilterChange,
    onFilterApply,
    onFilterReset,
    filterOpen,
    onFilterOpenChange,
    renderCard,
    renderGridTile,
    selecting = false,
    selectedIds,
    onToggleSelected,
    allIds,
    onSelectAll,
    selectAllLabel,
    onRowClick,
    getRowClassName,
    onEyeNavigate,
    emptyState,
    errorState,
    searchPlaceholder,
    clearSearchLabel,
    filterLabel,
    applyLabel,
    resetLabel,
    columnsLabel,
    sortLabel,
    toggleSortDirLabel,
    showingLabel,
    rowsPerPageLabel,
}: SectionDataViewProps<TRow>) {
    const isMobile = useIsMobile();
    const effectiveView: SectionView = isMobile ? "card" : view;

    const tableColumns = useMemo<ColumnDef<TRow, unknown>[]>(
        () =>
            buildSectionColumns(columns, {
                selecting,
                selectedIds,
                onToggleSelected,
                allIds,
                onSelectAll,
                selectAllLabel,
            }),
        [columns, selecting, selectedIds, onToggleSelected, allIds, onSelectAll, selectAllLabel],
    );

    const table = useReactTable<TRow>({
        data: data as TRow[],
        columns: tableColumns,
        pageCount: Math.max(1, Math.ceil(total / pageSize)),
        getRowId: (row) => row.id,
        state: { sorting, columnVisibility, pagination: { pageIndex, pageSize } },
        onSortingChange: (updater) =>
            onSortingChange(typeof updater === "function" ? updater(sorting) : updater),
        onColumnVisibilityChange: (updater) =>
            onColumnVisibilityChange(typeof updater === "function" ? updater(columnVisibility) : updater),
        manualPagination: true,
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const cardContextFor = (row: TRow): SectionCardContext => ({
        selecting,
        selected: selectedIds?.has(row.id) ?? false,
        onToggleSelected: () => onToggleSelected?.(row.id),
        onEyeNavigate,
    });

    // Local-state + debounced search: typing stays instant (no characters lost to the async
    // URL round-trip behind `query`), the actual search commits after a short quiet period.
    const search = useDebouncedSearchInput(query, onQueryChange);

    const rows = table.getRowModel().rows;
    const hasRows = rows.length > 0;

    return (
        <div className="flex flex-col">
            <SectionToolbar
                table={table}
                search={search}
                searchable={searchable}
                effectiveView={effectiveView}
                view={view}
                onViewChange={onViewChange}
                showGrid={Boolean(renderGridTile)}
                sorting={sorting}
                onSortingChange={onSortingChange}
                filterFields={filterFields}
                filterValues={filterValues}
                onFilterChange={onFilterChange}
                onFilterApply={onFilterApply}
                onFilterReset={onFilterReset}
                filterOpen={filterOpen}
                onFilterOpenChange={onFilterOpenChange}
                searchPlaceholder={searchPlaceholder}
                clearSearchLabel={clearSearchLabel}
                filterLabel={filterLabel}
                applyLabel={applyLabel}
                resetLabel={resetLabel}
                columnsLabel={columnsLabel}
                sortLabel={sortLabel}
                toggleSortDirLabel={toggleSortDirLabel}
            />

            <SectionContent
                isError={isError}
                isLoading={isLoading}
                hasRows={hasRows}
                rows={rows}
                table={table}
                effectiveView={effectiveView}
                renderCard={renderCard}
                renderGridTile={renderGridTile}
                cardContextFor={cardContextFor}
                onRowClick={onRowClick}
                getRowClassName={getRowClassName}
                emptyState={emptyState}
                errorState={errorState}
            />

            {hasRows ? (
                <DataTablePagination
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    total={total}
                    pageSizeOptions={pageSizeOptions}
                    selectorPageSize={selectorPageSize}
                    onPaginationChange={onPaginationChange}
                    showingLabel={showingLabel}
                    rowsPerPageLabel={rowsPerPageLabel}
                    className="px-4 py-2 md:px-8"
                />
            ) : null}
        </div>
    );
}
